import ow from 'ow';
import LoginViewState from './LoginViewState';
import { notBlankValidator } from './owValidators';
import {
  analytics,
  COMMON_PAGE_VIEWED_PAYLOAD_STRUCTURE,
  ELEMENT_CLICKED,
  ELEMENT_CLICKED_PROPERTIES,
  PAGE_TYPE,
} from './lib';

const LOCK_SIGN_IN_PAGE = 'login';
const LOCK_CREATE_ACCOUNT_PAGE = 'signUp';
const LOCK_FORGOT_PASSWORD_PAGE = 'forgotPassword';
const EMAIL_FIRST_PAGE = 'sso';

const MAINTENANCE_MODE_VIEW_STATE = new LoginViewState().withText({
  heading: "We'll be back soon!",
  subheading:
    "We're currently performing maintenance on this site. Please check back later today and we'll be glad to serve you!",
});

function LoginView(
  appName,
  emailFirstClient,
  maintenanceModeEnabled,
  txid,
  logError
) {
  const state = {
    email: undefined, // eslint-disable-line no-undefined
    maintenanceModeEnabled: maintenanceModeEnabled,
  };

  const screenText = () => ({
    email: {
      heading: 'Sign In',
      subheading: `to continue to ${appName}`,
    },
    login: {
      heading: 'Sign In',
      subheading: `to continue to ${appName}`,
    },
    signUp: {
      heading: 'Create Account',
      subheading: `to continue to ${appName}`,
    },
    forgotPassword: {
      heading: 'Forgot Password',
    },
  });

  const updateEmail = (email) => {
    if (typeof email === 'string' && email.trim()) {
      state.email = email.trim();
    } else {
      state.email = undefined; // eslint-disable-line no-undefined
    }
  };

  const additionalLockOptionOverrides = () => {
    if (!state.email) {
      return {};
    }
    return { prefill: { email: state.email } };
  };

  const showEmailForm = async (isErrorVisible = false) => {
    await analytics(
      'Sign In With SSO',
      {
        ...COMMON_PAGE_VIEWED_PAYLOAD_STRUCTURE,
        name: 'Sign In With SSO',
      },
      PAGE_TYPE
    );

    return new LoginViewState()
      .withEmail(state.email)
      .withScreenName('company')
      .withText(screenText().email)
      .withIsErrorVisible(isErrorVisible)
      .withIsEmailFormVisible(true)
      .withIsShowSignInPanelCompanyVisible(true);
  };
  const showSignIn = async () => {
    await analytics(
      'Sign In',
      {
        ...COMMON_PAGE_VIEWED_PAYLOAD_STRUCTURE,
        name: 'Sign In',
      },
      PAGE_TYPE
    );

    return new LoginViewState()
      .withEmail(state.email)
      .withScreenName('signIn')
      .withText(screenText().login)
      .withIsShowSignUpPanelVisible(true)
      .withIsSignInModalPanelVisible(true)
      .withLockState({
        lockOptionOverrides: {
          initialScreen: LOCK_SIGN_IN_PAGE,
          ...additionalLockOptionOverrides(),
        },
      });
  };
  const showSignUp = async () => {
    await analytics(
      'Sign Up',
      {
        ...COMMON_PAGE_VIEWED_PAYLOAD_STRUCTURE,
        name: 'Sign Up',
      },
      PAGE_TYPE
    );

    return new LoginViewState()
      .withEmail(state.email)
      .withScreenName('signUp')
      .withText(screenText().signUp)
      .withIsShowSignInPanelVisible(true)
      .withLockState({
        lockOptionOverrides: {
          initialScreen: LOCK_CREATE_ACCOUNT_PAGE,
          ...additionalLockOptionOverrides(),
        },
      });
  };

  const showForgotPassword = async () => {
    await analytics(
      'Forgot Password',
      {
        ...COMMON_PAGE_VIEWED_PAYLOAD_STRUCTURE,
        name: 'Forgot Password',
      },
      PAGE_TYPE
    );

    return new LoginViewState()
      .withEmail(state.email)
      .withScreenName('forgotPassword')
      .withText(screenText().forgotPassword)
      .withIsBackToSignInButtonVisible(true)
      .withLockState({
        lockOptionOverrides: {
          initialScreen: LOCK_FORGOT_PASSWORD_PAGE,
          ...additionalLockOptionOverrides(),
        },
      });
  };

  const showFederatedSignIn = async (email, federatedConnection) => {
    await analytics(
      'Federated Sign In',
      {
        ...COMMON_PAGE_VIEWED_PAYLOAD_STRUCTURE,
        name: 'Federated Sign In',
      },
      PAGE_TYPE
    );

    return new LoginViewState()
      .withEmail(email)
      .withFederatedConnection(federatedConnection);
  };
  const lookupEmail = async (email) => {
    ow(email, 'email', ow.string.validate(notBlankValidator));
    await analytics(ELEMENT_CLICKED, {
      ...ELEMENT_CLICKED_PROPERTIES,
      label: 'Continue - Email First Lookup',
      page: 'Sign In With SSO',
    });

    updateEmail(email);
    try {
      const id = await emailFirstClient.lookup(email, txid);
      if (id.isFederated) {
        return showFederatedSignIn(id.email, id.federatedConnection);
      }
      return showSignIn();
    } catch (e) {
      logError(e);
      return showEmailForm(true);
    }
  };

  const init = async (emailLoginHint, requestedScreen = LOCK_SIGN_IN_PAGE) => {
    await analytics(
      'Auth0 Init',
      {
        ...COMMON_PAGE_VIEWED_PAYLOAD_STRUCTURE,
        search: `display=${requestedScreen}`,
        name: 'Auth0 Init',
      },
      PAGE_TYPE
    );

    if (state.maintenanceModeEnabled) {
      return MAINTENANCE_MODE_VIEW_STATE;
    }

    updateEmail(emailLoginHint);

    if (requestedScreen === EMAIL_FIRST_PAGE) {
      return showEmailForm();
    } else if (requestedScreen === LOCK_CREATE_ACCOUNT_PAGE) {
      return showSignUp();
    }

    return showSignIn();
  };

  return {
    emailForm: async () => showEmailForm(),
    signInForm: async () => showSignIn(),
    signUpForm: async () => showSignUp(),
    onEmailFormSubmit: async (email) => await lookupEmail(email),
    forgotPassword: async () => showForgotPassword(),
    init,
  };
}

export default LoginView;
